import { Injectable, signal, inject } from '@angular/core';
import { NotificationService } from './notification';
import { collection, query, where, getDocs, collectionGroup } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { db, auth } from './firebase';
import { AuthProfile } from './models/auth.model';
import { Chat, ChatService } from './services/chat.service';
import { UserImage } from './models/image.model';
import { News } from './models/news.model';

export interface SearchResult {
  title: string;
  snippet: string;
  url: string;
}

export interface SearchResponse {
  answer: string;
  sources: SearchResult[];
  accounts?: AuthProfile[];
  chats?: Chat[];
  userImages?: UserImage[];
  news?: News[];
}

@Injectable({
  providedIn: 'root'
})
export class SearchService {
  private notificationService = inject(NotificationService);
  private chatService = inject(ChatService);
  
  loading = signal(false);
  response = signal<SearchResponse | null>(null);
  
  private unsubscribeChats: (() => void) | null = null;

  constructor() {
    if (typeof window !== 'undefined') {
      onAuthStateChanged(auth, (user) => {
        if (!user && this.unsubscribeChats) {
          this.unsubscribeChats();
          this.unsubscribeChats = null;
        }
      });
    }
  }

  async search(queryText: string, category = 'all') {
    this.loading.set(true);
    this.response.set(null);

    try {
      let accounts: AuthProfile[] = [];
      let chats: Chat[] = [];
      let userImages: UserImage[] = [];
      let news: News[] = [];
      const currentUser = auth.currentUser;

      // Fetch accounts if category is compatible
      if (category === 'web') {
        accounts = [];
        chats = [];
        userImages = [];
      } else {
        if (category === 'person' || category === 'organization' || category === 'country' || category === 'all') {
            try {
              const accountsRef = collection(db, 'accounts');
              if (category === 'country') {
                const [snap1, snap2] = await Promise.all([
                  getDocs(query(accountsRef, where('accountType', '==', 'country'))).catch(() => ({ docs: [] })),
                  getDocs(query(accountsRef, where('account_type', '==', 'country'))).catch(() => ({ docs: [] }))
                ]);
                const countryMap = new Map<string, AuthProfile>();
                snap1.docs.forEach(d => {
                  const data = d.data() as AuthProfile;
                  countryMap.set(d.id, { ...data, uid: d.id, accountType: 'country' });
                });
                snap2.docs.forEach(d => {
                  const data = d.data() as AuthProfile;
                  countryMap.set(d.id, { ...data, uid: d.id, accountType: 'country' });
                });
                const allCountryAccounts = Array.from(countryMap.values());
                if (queryText.trim()) {
                  const lowerQuery = queryText.toLowerCase();
                  accounts = allCountryAccounts.filter(acc => 
                    (acc.displayName && acc.displayName.toLowerCase().includes(lowerQuery)) || 
                    (acc.name && acc.name.toLowerCase().includes(lowerQuery)) ||
                    (acc.username && acc.username.toLowerCase().includes(lowerQuery)) ||
                    (acc.countryCode && acc.countryCode.toLowerCase().includes(lowerQuery))
                  );
                } else {
                  accounts = allCountryAccounts;
                }
              } else {
                let q;
                if (category === 'person') {
                  q = query(accountsRef, where('accountType', '==', 'person'), where('status', '==', 'active'));
                } else if (category === 'organization') {
                  q = query(accountsRef, where('accountType', '==', 'organization'), where('status', '==', 'active'));
                } else {
                  q = query(accountsRef, where('status', '==', 'active'));
                }
                const querySnapshot = await getDocs(q);
                const allAccounts = querySnapshot.docs.map(doc => {
                  const data = doc.data() as AuthProfile;
                  const accType = data.accountType || (data as any).account_type;
                  return { ...data, uid: doc.id, accountType: accType } as AuthProfile;
                });
                if (queryText.trim()) {
                  const lowerQuery = queryText.toLowerCase();
                  accounts = allAccounts.filter(acc => (acc.displayName && acc.displayName.toLowerCase().includes(lowerQuery)) || (acc.username && acc.username.toLowerCase().includes(lowerQuery)));
                } else {
                  accounts = allAccounts.slice(0, 6);
                }
              }
            } catch (err) { console.error("Error fetching accounts:", err); }
        }

        if (this.unsubscribeChats) {
          this.unsubscribeChats();
          this.unsubscribeChats = null;
        }

        if (currentUser && (category === 'chats' || category === 'all')) {
            try {
              this.unsubscribeChats = this.chatService.listenToMyChats(currentUser.uid, (allChats) => {
                let filteredChats = allChats;
                if (queryText.trim()) {
                  const lowerQuery = queryText.toLowerCase();
                  filteredChats = allChats.filter(chat => (chat.participantName && chat.participantName.toLowerCase().includes(lowerQuery)) || (chat.lastMessage && chat.lastMessage.toLowerCase().includes(lowerQuery)));
                }
                
                chats = filteredChats;
                const currentResponse = this.response();
                if (currentResponse) {
                  this.response.set({ ...currentResponse, chats: filteredChats });
                }
              });
            } catch (err) { console.error("Error fetching chats:", err); }
        }

        // Fetch News if category is news or all
        if (category === 'news' || category === 'all') {
            try {
              const newsQuery = collectionGroup(db, 'news');
              const newsSnap = await getDocs(newsQuery);
              const allNews = newsSnap.docs
                .map(doc => {
                  const id = doc.id;
                  const parentId = doc.ref.parent.parent?.id || '';
                  const data = doc.data() as any;
                  const content = data.content || data.description || '';
                  const description = data.description || data.content || '';
                  let bannerImage = data.bannerImage || data.imageUrl || '';
                  if (Array.isArray(bannerImage)) {
                    bannerImage = bannerImage[0] || '';
                  }
                  if (!bannerImage && Array.isArray(data.images) && data.images.length > 0) {
                    bannerImage = data.images[0] || '';
                  }
                  if (!bannerImage && Array.isArray(data.galleryImages) && data.galleryImages.length > 0) {
                    bannerImage = data.galleryImages[0] || '';
                  }
                  return {
                    ...data,
                    id,
                    ownerUid: parentId,
                    ownerId: parentId,
                    content,
                    description,
                    bannerImage
                  } as any;
                })
                .filter(n => n.status === 'published' && n.verificationStatus === 'approved');

              // Sort in memory by createdAt descending
              allNews.sort((a, b) => {
                const getVal = (item: News) => {
                  const d = item.createdAt || item.createdAt;
                  if (!d) return 0;
                  if (typeof d === 'object' && d !== null && 'seconds' in d) {
                    return d.seconds;
                  }
                  if (d instanceof Date) return d.getTime() / 1000;
                  return new Date(d as string | number).getTime() / 1000;
                };
                return getVal(b) - getVal(a);
              });

              if (queryText.trim()) {
                const lowerQuery = queryText.toLowerCase();
                news = allNews.filter(n => 
                  (n.title && n.title.toLowerCase().includes(lowerQuery)) ||
                  (n.description && n.description.toLowerCase().includes(lowerQuery)) ||
                  (n.category && n.category.toLowerCase().includes(lowerQuery))
                );
              } else {
                news = allNews;
              }
            } catch (err) { console.error("Error fetching news:", err); }
        }

        // Fetch UserImages if category is images or all
        if (category === 'images' || category === 'all') {
            try {
              const matchedAccountUids = new Set<string>();
              const qText = queryText.trim().toLowerCase();
              if (qText) {
                const accountsSnap = await getDocs(collection(db, 'accounts'));
                accountsSnap.docs.forEach(doc => {
                  const acc = doc.data();
                  const displayName = (acc['displayName'] || '').toLowerCase();
                  const username = (acc['username'] || '').toLowerCase();
                  if (displayName.includes(qText) || username.includes(qText)) {
                    matchedAccountUids.add(doc.id);
                  }
                });
              }

              const imgQuery = collectionGroup(db, 'images');
              const imgSnap = await getDocs(imgQuery);
              const allUserImages = imgSnap.docs
                .map(doc => {
                  const data = doc.data();
                  const parentId = doc.ref.parent?.parent?.id || '';
                  return {
                    id: doc.id,
                    imageUrl: data['imageUrl'] || '',
                    visibility: data['visibility'] || 'public',
                    status: data['status'] || 'unpublished',
                    verificationStatus: data['verificationStatus'] || 'review',
                    license: data['license'] || 'standard',
                    createdAt: data['createdAt'],
                    likes: data['likes'] || [],
                    views: data['views'] || [],
                    likedBy: data['likedBy'] || data['likes'] || [],
                    viewedBy: data['viewedBy'] || data['views'] || [],
                    ownedBy: data['ownedBy'] || data['ownerUid'] || parentId,
                    uploadedBy: data['uploadedBy'] || data['authorUid'] || parentId,
                    authorUid: data['authorUid'] || parentId,
                    ownerUid: data['ownerUid'] || parentId,
                  } as any;
                })
                .filter(img => 
                  (img.status === 'approved' || img.verificationStatus === 'approved') &&
                  img.visibility === 'public'
                );

              if (qText) {
                userImages = allUserImages.filter(img => 
                  ((img.remark || img.remarks) && (img.remark || img.remarks)!.toLowerCase().includes(qText)) ||
                  (img.authorUid && matchedAccountUids.has(img.authorUid)) ||
                  (img.ownerUid && matchedAccountUids.has(img.ownerUid))
                );
              } else {
                userImages = allUserImages;
              }
            } catch (err) {
              console.error("Error searching user images:", err);
            }
        }
      }

      // Simplified web results display: show only direct matches/results without AI overview.
      this.response.set({ 
        answer: "Web results are shown below.",
        sources: [{ title: `Results for '${queryText}'`, snippet: "Web content results based on search input.", url: "#" }],
        accounts: category === 'web' ? [] : accounts,
        chats: category === 'web' ? [] : chats,
        userImages: category === 'web' ? [] : userImages,
        news: category === 'web' ? [] : news
      });
      
    } catch (err: unknown) {
      console.error("Search error:", err);
      this.notificationService.error("Failed to perform search. Please try again.");
      this.response.set({ answer: "An error occurred while searching.", sources: [] });
    } finally {
      this.loading.set(false);
    }
  }
}
